import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_EMAIL, ADMIN_ACCESS_CODE, ADMIN_AUTH_EXPIRATION, isAdmin } from '../utils/adminConfig';
import './Admin.css';

const AdminAuth = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [adminCode, setAdminCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Check if user is already an admin
    const checkAdmin = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      // Check if this is the designated admin email
      if (!isAdmin(currentUser)) {
        setError('Only the designated administrator can access this page.');
        return;
      }
      
      try {
        // Check if local admin mode is enabled
        const localAdminAuth = localStorage.getItem('adminAuth');
        if (localAdminAuth) {
          const adminData = JSON.parse(localAdminAuth);
          if (adminData.userId === currentUser.uid && adminData.expires > Date.now()) {
            console.log('Admin access granted from local storage');
            navigate('/admin');
            return;
          } else {
            // Clean up expired admin auth
            localStorage.removeItem('adminAuth');
          }
        }
        
        // Check database role
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists() && userSnap.data().role === 'admin') {
          console.log('User is already an admin, redirecting to admin panel');
          navigate('/admin');
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
      }
    };
    
    checkAdmin();
  }, [currentUser, navigate]);
  
  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!adminCode) {
      setError('Please enter the admin access code');
      return;
    }
    
    // Check if this is the designated admin email
    if (!isAdmin(currentUser)) {
      setError('Only the designated administrator can access this page.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (adminCode === ADMIN_ACCESS_CODE) {
        console.log('Admin code verified');
        
        try {
          // First, try the direct update method
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, { 
            role: 'admin',
            updatedAt: new Date()
          });
          
          setSuccess('Access granted! Redirecting to admin panel...');
          
          // Short delay before redirecting to let user see success message
          setTimeout(() => {
            // Force reload to refresh user data
            window.location.href = '/admin';
          }, 1500);
        } catch (updateError) {
          console.error('Error updating user role:', updateError);
          
          // Store admin authentication in localStorage with 24-hour expiration
          const adminAuth = {
            userId: currentUser.uid,
            email: currentUser.email,
            grantedAt: Date.now(),
            expires: Date.now() + ADMIN_AUTH_EXPIRATION
          };
          
          localStorage.setItem('adminAuth', JSON.stringify(adminAuth));
          
          setSuccess('Admin access granted using local authentication! Redirecting...');
          
          setTimeout(() => {
            window.location.href = '/admin';
          }, 1500);
        }
      } else {
        setError('Invalid admin code. Please try again.');
      }
    } catch (err) {
      console.error('Error in verification process:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="admin-login-container">
      <div className="admin-login-form">
        <h2>Admin Access</h2>
        <p>Enter the admin access code to continue</p>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {currentUser && isAdmin(currentUser) ? (
          <form onSubmit={handleVerify}>
            <input 
              type="password"
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value)}
              placeholder="Enter admin code"
              disabled={loading || success}
            />
            
            <button 
              type="submit" 
              disabled={loading || success}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={() => navigate('/')}
              disabled={loading || success}
            >
              Cancel
            </button>
          </form>
        ) : (
          <div className="unauthorized-message">
            <p>You are not authorized to access the admin panel.</p>
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuth; 